/**
 * MaskedPhoneInput
 *
 * The storefront's phone field, wired for react-hook-form. Carries no
 * label/error markup and no styling of its own — every caller passes the
 * classes that match its own form (checkout and auth look different).
 *
 * The mask is a DISPLAY device only. What leaves this component through
 * `onChange` is the canonical phone ("13105550147" — digits with country code,
 * no plus, see lib/phone.ts), so no form, request body or database column ever
 * sees the punctuation. Symmetrically, a canonical `value` coming in is shown
 * through `formatPhone`.
 *
 * THE SERVICE IS US-ONLY, so the mask is the single US template and there is no
 * country picker: the canon itself is /^1[2-9][0-9]{9}$/. The one subtlety is
 * what happens to input that does NOT fit that template. It must not be
 * squeezed into it: "447911123456" pushed through "+1 (000) 000-0000" comes out
 * as "+1 (447) 911-1234" → canon "14479111234", i.e. a DIFFERENT, real US
 * number quietly taking the customer's place in `customers.phone` (their
 * identity). So overlong input is kept verbatim behind a bare '+' — not as
 * support for other countries, but so validation can reject it by name
 * ("Enter a US phone number", lib/validations/phone.ts) instead of the mask
 * turning it into someone else's number. That verbatim mode is sticky until the
 * field is emptied: otherwise deleting one digit from such a number would snap
 * the rest into US punctuation mid-edit — the same substitution, one keystroke
 * later.
 *
 * The second half of the same guarantee: `onChange` fires only for input the
 * customer actually made. IMask also emits `accept` when the value is set
 * programmatically (mount, a profile prefill, a form reset) — and it passes the
 * originating InputEvent only in the first case, which is exactly the signal
 * used here. Without it the component would report a value nobody typed, on
 * mount, before any interaction.
 *
 * Why the display is kept in local state instead of re-deriving it from `value`
 * on every render: mid-typing the canon is a partial number ("1310555"), and
 * feeding that back into IMask would make the input fight the person typing —
 * characters would be re-masked, dropped or the caret thrown to the end. So we
 * keep what IMask produced, and only re-sync when the value changes from the
 * OUTSIDE (a profile prefill, a form reset) — recognized by comparing against
 * the last canon this component itself emitted. That comparison happens during
 * render, not in an effect: it is React's "adjusting state when a prop changes"
 * pattern, and it avoids rendering the stale number for one frame.
 */
'use client'

import { forwardRef, useState } from 'react'
import { IMaskInput } from 'react-imask'
import { formatPhone, normalizePhone } from '@/lib/phone'

export interface MaskedPhoneInputProps {
  name: string
  id?: string
  className?: string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  'aria-invalid'?: boolean
  /** Canonical phone (or '') — never a formatted string. */
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
}

const US_MASK = '+1 (000) 000-0000'
/**
 * Not a second country: the escape hatch described above. Digits that the US
 * template cannot hold stay as they were typed, so validation sees the real
 * number and can reject it.
 */
const VERBATIM_MASK = `+${'0'.repeat(15)}`
/**
 * IMask's own dispatch picks the first mask that swallows the whole input, so
 * "3105550147" and "13105550147" get the US template while "447911123456"
 * falls through to the verbatim one — no digit is dropped or re-interpreted.
 */
const MASKS = [{ mask: US_MASK }, { mask: VERBATIM_MASK }]
/** The same list with the US template dropped — the sticky verbatim mode. */
const VERBATIM_ONLY = [{ mask: VERBATIM_MASK }]

/** Does this text still fit "+1 (000) 000-0000" (10 digits, or 11 behind a 1)? */
function fitsUsMask(text: string): boolean {
  const digits = text.replace(/\D/g, '')
  return digits.length <= 10 || (digits.length === 11 && digits.startsWith('1'))
}

export const MaskedPhoneInput = forwardRef<HTMLInputElement, MaskedPhoneInputProps>(
  ({ name, id, className, placeholder = '+1 (555) 123-4567', disabled, required, value, onChange, onBlur, ...rest }, ref) => {
    // `formatPhone` only ever returns a US number or '', so a value arriving
    // from outside always fits the US template — the verbatim mode below is
    // reachable from typing/pasting alone.
    const [display, setDisplay] = useState(() => formatPhone(value))
    const [prevValue, setPrevValue] = useState(value)
    const [verbatim, setVerbatim] = useState(false)

    // React's "adjust state when a prop changes" pattern: react to the prop
    // actually changing, and only re-render the display when the new value
    // means a different number than what is on screen. Without that second
    // guard the form echoing back the canon we just emitted ("1310555" while
    // the customer is still typing) would reformat the field under their
    // fingers — the whole reason the display is not derived on every render.
    if (value !== prevValue) {
      setPrevValue(value)
      if (normalizePhone(value) !== normalizePhone(display)) {
        setDisplay(formatPhone(value))
        setVerbatim(false)
      }
    }

    return (
      <IMaskInput
        mask={verbatim ? VERBATIM_ONLY : MASKS}
        value={display}
        unmask={false}
        onAccept={(accepted: string, _mask: unknown, event?: InputEvent) => {
          setDisplay(accepted)
          // Sticky: stay verbatim until the field is cleared, so editing a
          // rejected number does not bounce into US punctuation the moment it
          // is briefly short enough to fit.
          setVerbatim((wasVerbatim) =>
            wasVerbatim ? accepted.replace(/\D/g, '') !== '' : !fitsUsMask(accepted)
          )
          // No InputEvent → the value was set programmatically (mount, prefill,
          // reset). Show it, but never report it as a change the customer made.
          if (!event) return
          const canonical = normalizePhone(accepted)
          // Synthetic event so react-hook-form's register()/field sees a normal change
          onChange?.({ target: { name, value: canonical } } as React.ChangeEvent<HTMLInputElement>)
        }}
        onBlur={onBlur}
        inputRef={ref}
        id={id ?? name}
        name={name}
        type="tel"
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        aria-required={required}
        {...rest}
      />
    )
  }
)

MaskedPhoneInput.displayName = 'MaskedPhoneInput'
